import { useState, useEffect, Fragment } from 'react';
import Layout from '../../components/layout';
import TimesheetSummary from '../../api/models/timesheetSummary';
import EditModal from '../../components/editModal';
import TimesheetWeekTask from '../../api/models/TimesheetWeekTask';
import { getTimesheetWeek, updateFlagStatus } from '../../api/services/timesheetApprovalProofingService';
import { useLocation, useParams } from 'react-router-dom';
import TimesheetFlag from '../../api/models/timesheetFlag';
import TimesheetWeekEntry from '../../api/models/TimesheetWeekEntry';
import { useAsync } from 'react-async';
import TimesheetEntryFlagRequest from '../../api/requests/timesheetEntryFlagRequest';
import { TaskTypeLookup, TimesheetFlagStatus } from '../../common/constants';
import TimesheetEntryFlag from '../../api/models/timesheetEntryFlag';
import { TimesheetProofingDistrictModel } from "../../api/models/timesheetProofingDistrictModel";
import { hasValue, removeDuplicates } from '../../common/utils';
import { TimesheetProofingWeekModel } from '../../api/models/timesheetProofingWeekModel';
import React from 'react';
import DataTable from '../../components/dataTable';
import SelectMulti from 'react-select';
import SubmitButton from '../../components/submitButton';
import { getWeek } from '../../api/services/timesheetService';
import TimesheetProofingDistrictRequest from '../../api/requests/timesheetProofingDistrictRequest';
import { getTimesheetProofingDistrict } from '../../api/services/timesheetProofingService';
import moment from 'moment';
import Student from '../../api/models/student';

type Params = {
  id: string;
};


type ViewState = {
  district?: TimesheetProofingDistrictModel;
};

class FlagRow {
  id?: number;
  type?: string = "";
  discipline?: string = "";
  therapist?: string = "";
  school?: string = "";
  entry?: string = "";
  task?: string = "";
  date?: string = "";
  mins?: string = "";
  flag?: string = "";
  feedback?: string = "";

  fullEntry?: TimesheetWeekEntry;
  fullTask?: TimesheetWeekTask;
  fullEntryFlag?: TimesheetEntryFlag;
  fullTimesheetFlag?: TimesheetFlag;

  weekId?: number = 0;
  compareDate?: number = 0;
  students?: Student[] = [];

  public equals(obj: FlagRow): boolean {
    return this.id === obj.id;
  }
};


/**
 * TODO: This needs to be refactored.
 * This is calling at least a dozen async calls, if not more, and can crash the browser
 */
const fetchWeeks = async (args: any[]) => {
  let promises = []
  for (let i = 0; i < args[0].length; i++) {
    promises.push(getTimesheetWeek(Number(args[0][i].weekId)).then(function (r) {
      let ts = r.data.timesheets;
      ts.forEach((tSummary: TimesheetSummary, idx: number, arr: TimesheetSummary[]) => {
        arr[idx].queriedWeekId = Number(args[0][i].weekId);
      });
      return ts;
    }));
  }
  return Promise.all(promises);
}

const ProofingSummary = () => {
  //constants
  const { id } = useParams<Params>();
  const { state } = useLocation<ViewState>();

  //DataTable Columns
  const columns = React.useMemo(
    () => [
      {
        accessor: 'id',
        width: 30,
        disableSortBy: true,
        Header: (props: any) => {
          return (
            <input
              type='checkbox'
              onChange={(e) => {
                const checkboxes = document.querySelectorAll('.datatable-checkbox');

                checkboxes.forEach((checkbox: any) => {
                  if (e.currentTarget.checked) {
                    if (!checkbox.checked) checkbox.click();
                  } else {
                    if (checkbox.checked) checkbox.click();
                  }
                });
              }}
            />
          )
        },
        Cell: (props: any) => {
          return (
            <input
              className='datatable-checkbox'
              value={(props.row.original as FlagRow).id}
              type='checkbox'
              onChange={(e) => {
                const checkboxes = document.querySelectorAll('.datatable-checkbox');
                let anyOn = false;
                checkboxes.forEach((checkbox: any) => {
                  if (checkbox.checked) {
                    anyOn = true;
                  }
                });
                if (anyOn) {
                  setSelectedCount(1);
                } else {
                  setSelectedCount(0);
                }
              }}
            />
          )
        }
      },
      {
        id: 'disciplineColumn',
        Header: 'Discipline',
        accessor: 'discipline',
      },
      {
        id: 'therapistColumn',
        Header: 'Therapist',
        accessor: 'therapist',
      },
      {
        id: 'schoolColumn',
        Header: 'School',
        accessor: 'school',
      },
      {
        id: 'entryColumn',
        Header: 'Entry',
        accessor: 'entry',
      },
      {
        id: 'taskColumn',
        Header: 'Task',
        accessor: 'task',
      },
      {
        id: 'dateColumn',
        Header: 'Date',
        accessor: 'date',
        sortType: (rowA: any, rowB: any, id: string, desc: any) => {
          return rowA.original.compareDate - rowB.original.compareDate;
        }
      },
      {
        id: 'minsColumn',
        Header: 'Mins',
        accessor: 'mins',
      },
      {
        id: 'flagColumn',
        Header: 'Flag',
        accessor: 'flag',
      },
      {
        id: 'actionColumn',
        Header: 'Actions',
        disableSortBy: true,
        Cell: (props: any) => {
          let row = props.row.original as FlagRow;
          return (
            <div>
              <div><a href="#" onClick={(e) => { handleApprove(e, row); }}>Approve</a></div>
              <div><a href="#" onClick={(e) => { handleReject(e, row); }}>Reject</a></div>
              <div><a href="#" onClick={(e) => { handleRejectWFeedback(e, row); }}>Reject w/Feedback</a></div>
            </div>
          )
        }
      },
      {
        id: 'feedbackColumn',
        Header: 'Rejection Feedback',
        accessor: 'feedback',
      },
    ],
    []);

  //Initial DataTable Sorting State
  const defaultSort = React.useMemo(
    () => [
      {
        id: "disciplineColumn",
        desc: false
      }
    ],
    []
  );
  const [initialState, setInitialState] = useState<{}>({ sortBy: defaultSort, sortType: "ci" });

  //Timesheet hooks  
  const [district, setDistrict] = useState<TimesheetProofingDistrictModel>();
  const [weeks, setWeeks] = useState<TimesheetProofingWeekModel[]>();
  const [flags, setFlags] = useState<FlagRow[]>([]);
  const [originalFlags, setOriginalFlags] = useState<FlagRow[]>([]);

  const [therapistOptions, setTherapistOptions] = useState<{ label: string, value: string }[]>([]);
  const [therapist, setTherapist] = useState<string>("-1");

  const [disciplineOptions, setDisciplineOptions] = useState<{ label: string, value: string }[]>([]);
  const [discipline, setDiscipline] = useState<string>("-1");

  const [flagOptions, setFlagOptions] = useState<{ label: string, value: string }[]>([]);
  const [flag, setFlag] = useState<string>("-1");

  const [schoolOptions, setSchoolOptions] = useState<{ label: string, value: string }[]>([]);
  const [school, setSchool] = useState<string>("-1");

  const [timesheetSummaries, setTimesheetSummaries] = useState<TimesheetSummary[][]>();

  const [openReviewEntryModal, setOpenReviewEntryModal] = useState<boolean>(false);
  const [task, setTask] = useState<TimesheetWeekTask>();
  const [entry, setEntry] = useState<TimesheetWeekEntry>();
  const [feedback, setFeedback] = useState<string>();
  const [timesheetFlag, setTimesheetFlag] = useState<TimesheetFlag>();
  const [entryFlag, setEntryFlag] = useState<TimesheetEntryFlag>();
  const [selectedFlag, setSelectedFlag] = useState<FlagRow>();
  const [entryFlagAction, setEntryFlagAction] = useState<string>();
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [selectedMonth, setSelectedMonth] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [studentData, setStudentData] = useState<Student[]>([]);

  const [viewingStringFrom, setViewingStringFrom] = useState<string>("");
  const [viewingStringTo, setViewingStringTo] = useState<string>("");
  const [lastTimesheet, setLastTimesheet] = useState<TimesheetSummary>();

  //Runs the asyn on page load
  useEffect(() => {
    // This is not the canonical React approach; this should be eventually updated
    setTimeout(() => load.run(), 1);
  }, []);

  //When the timesheet summaries are loaded, parse them
  useEffect(() => {
    if (timesheetSummaries?.length && timesheetSummaries?.length > 0) {
      setLastTimesheet(timesheetSummaries[timesheetSummaries.length - 1][0]);
      parseResponse();
    }

  }, [timesheetSummaries]);

  //Get the week data
  useEffect(() => {
    weekData.run(weeks);
  }, [weeks]);

  //After we get the week data, set the timesheet summaries
  const weekData = useAsync({
    deferFn: fetchWeeks,
    onResolve: (response: TimesheetSummary[][]) => {
      if (response) {
        setTimesheetSummaries(response);
      }
    }
  });

  //When the district changes, parse the timesheets... again
  useEffect(() => {
    if (district != undefined) {
      let the_weeks: TimesheetProofingWeekModel[] = [];
      for (var week of district.weeks) {
        if (viewingStringFrom === "") {
          let dates = week.weekRangeDisplay.split(" - ");
          getWeek(week.weekId).then((response) => {
            let weekFirst = new Date(response.data.startOfWeek);
            let parsed = new Date(Date.parse(dates[0] + "/" + weekFirst.getFullYear()));

            let month = parsed.getMonth() + 1;
            setSelectedMonth(month);
            if (viewingStringFrom === "") {

              setSelectedYear(weekFirst.getFullYear());
              let lastDay = new Date(weekFirst.getFullYear(), month, 0);
              setViewingStringFrom(month + "/1" + "/" + weekFirst.getFullYear().toString().substring(2));
              setViewingStringTo(month + "/" + lastDay.getDate() + "/" + weekFirst.getFullYear().toString().substring(2));
            }
          });
        }
        if (week.timesheetEntryFlagsCount > 0) {
          the_weeks.push(week);
        }
      }

      setWeeks(the_weeks);
    }
  }, [district]);

  /**
   * Parses the timesheets
   */
  const parseResponse = () => {
    var therapistOptions: { label: string, value: string }[] = [];
    var disciplineOptions: { label: string, value: string }[] = [];
    var flagOptions: { label: string, value: string }[] = [];
    var schoolOptions: { label: string, value: string }[] = [];
    var newFlags: FlagRow[] = [];
    timesheetSummaries?.forEach((element: TimesheetSummary[]) => {
      var parsed = parseTimesheet(element[0], therapistOptions, disciplineOptions, flagOptions, schoolOptions);
      therapistOptions = parsed.therapistOptions;
      disciplineOptions = parsed.disciplineOptions;
      flagOptions = parsed.flagOptions;
      schoolOptions = parsed.schoolOptions;

      parsed.flags.forEach((fl) => {
        let contains = false;
        newFlags.forEach((nf) => {
          if (nf.equals(fl)) {
            contains = true;
          }
        });

        if (!contains) {
          fl.compareDate = parseInt(moment(fl.date!).format("YYYYMMDD"));
          newFlags.push(fl);
        }
      });
    });

    setFlags(newFlags);
    setOriginalFlags(newFlags);
    setTherapistOptions(therapistOptions);
    setDisciplineOptions(disciplineOptions);
    setSchoolOptions(schoolOptions);
    setFlagOptions(flagOptions);
  };

  const parseTimesheet = (el: TimesheetSummary, therapistOptions: { label: string, value: string }[], disciplineOptions: { label: string, value: string }[], flagOptions: { label: string, value: string }[], schoolOptions: { label: string, value: string }[]) => {
    setLastTimesheet(el);
    var flags: FlagRow[] = [];
    el?.weeks?.forEach((wk) => {
      wk.items?.forEach((i) => {
        if (i.districtId === district?.districtId) {
          i.tasks?.forEach((t) => {
            t.entries?.forEach((e) => {
              if (e.timesheetEntryFlags.length > 0) {
                e.timesheetEntryFlags.forEach((ef) => {

                  let flag = new FlagRow();
                  flag.fullEntry = e;
                  flag.fullTask = t;
                  flag.fullEntryFlag = ef;
                  flag.weekId = wk.weekId;
                  flag.students = i.studentData;
                  let name = el.therapist?.user?.lastName + ", " + el.therapist?.user?.firstName;
                  let dis = el.therapist?.discipline?.name;
                  let fl = ef.proofingRule.name;
                  let school = i.subTitle;

                  flag.id = ef.timesheetEntryFlagId;
                  flag.type = "entry";
                  flag.discipline = dis;
                  flag.therapist = name;

                  if (therapistOptions.filter(e => e.label === name).length == 0) {
                    therapistOptions.push({ label: name, value: name });
                  }
                  if (disciplineOptions.filter(e => e.label === dis).length == 0) {
                    disciplineOptions.push({ label: dis ?? "", value: dis ?? "" });
                  }
                  if (flagOptions.filter(e => e.label === fl).length == 0) {
                    flagOptions.push({ label: fl, value: fl });
                  }
                  if (schoolOptions.filter(e => e.label === school).length == 0) {
                    schoolOptions.push({ label: school, value: school });
                  }

                  flag.school = school;
                  flag.entry = t.entryTypeName.toString();
                  flag.task = t.taskTypeName.toString();
                  flag.flag = fl;
                  flag.mins = e.minutes?.toString() ?? '0';
                  flag.feedback = ef.feedback;

                  let d = new Date(e.dateOfEntry);
                  flag.date = (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear();

                  flags.push(flag);
                });
              }
            });
          });
        }


      });
    });

    return {
      flags: flags,
      therapistOptions: therapistOptions,
      disciplineOptions: disciplineOptions,
      flagOptions: flagOptions,
      schoolOptions: schoolOptions,
    };
  };

  /**
   * Search handler
   */
  const handleSearch = async () => {
    let flags = originalFlags;

    if (therapist != "-1") {
      flags = flags.filter(x => x.therapist == therapist);
    }
    if (discipline != "-1") {
      flags = flags.filter(x => x.discipline == discipline);
    }
    if (school != "-1") {
      flags = flags.filter(x => x.school == school);
    }
    if (flag != "-1") {
      flags = flags.filter(x => x.flag == flag);
    }

    setFlags(flags);
    return new Promise<void>((resolve) => resolve());
  };
  const handleSearchAsync = useAsync({ deferFn: handleSearch });


  /**
   * Loads the timesheets
   */
  const doLoad = async () => {
    setDistrict((hasValue(state.district) ? state.district : null) as TimesheetProofingDistrictModel);
    return null;
  };

  const load = useAsync({
    deferFn: doLoad,
  });

  /**
   * Update/Approve/Reject Logic
   */
  const updateFlag = async (request: TimesheetEntryFlagRequest) => {
    //Wait for our response
    return updateFlagStatus(request);
  }


  const rejectMulti = useAsync({
    deferFn: async () => {
      const checkboxes = document.querySelectorAll('.datatable-checkbox');
      let promises: Promise<void>[] = [];
      checkboxes.forEach((checkbox: any) => {
        if (checkbox.checked) {

          let flag = flags.find(x => x.id == checkbox.value);
          if (flag !== undefined) {
            let request: TimesheetEntryFlagRequest = {
              timesheetEntryFlagId: flag.id,
              timesheetEntryFlagStatusId: TimesheetFlagStatus.Rejected,
              timesheetWeekId: flag.weekId,
              feedback: feedback
            }

            promises.push(updateFlag(request).then((response) => {
              setEntryFlag(undefined);
              setFeedback('');
              return new Promise<void>((resolve) => resolve());
            }));
          }
        }
      });

      return Promise.all(promises);
    },
    onResolve: () => {
      const checkboxes = document.querySelectorAll('.datatable-checkbox');
      checkboxes.forEach((checkbox: any) => {
        if (checkbox.checked) {
          checkbox.click();
        }
      });
      const request: TimesheetProofingDistrictRequest = {
        districtId: district!.districtId,
        year: selectedYear,
        month: selectedMonth,
      }
      getTimesheetProofingDistrict(request).then((response) => {
        setDistrict(response.data.records[0]);
      });
      alert("Selected entries rejected.");
    }
  });


  const approveMulti = useAsync({
    deferFn: async () => {
      const checkboxes = document.querySelectorAll('.datatable-checkbox');
      let promises: Promise<void>[] = [];
      checkboxes.forEach((checkbox: any) => {
        if (checkbox.checked) {

          let flag = flags.find(x => x.id == checkbox.value);
          if (flag !== undefined) {
            let request: TimesheetEntryFlagRequest = {
              timesheetEntryFlagId: flag.id,
              timesheetEntryFlagStatusId: TimesheetFlagStatus.Approved,
              timesheetWeekId: flag.weekId,
              feedback: feedback
            }

            promises.push(updateFlag(request).then((response) => {
              setEntryFlag(undefined);
              setFeedback('');
              return new Promise<void>((resolve) => resolve());
            }));
          }
        }
      });

      return Promise.all(promises);
    },
    onResolve: () => {
      const checkboxes = document.querySelectorAll('.datatable-checkbox');
      checkboxes.forEach((checkbox: any) => {
        if (checkbox.checked) {
          checkbox.click();
        }
      });
      const request: TimesheetProofingDistrictRequest = {
        districtId: district!.districtId,
        year: selectedYear,
        month: selectedMonth,
      }
      getTimesheetProofingDistrict(request).then((response) => {
        setDistrict(response.data.records[0]);
      });
      alert("Selected entries approved.");
    }
  });
  const handleRejectWFeedback = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, row: FlagRow) => {
    e.preventDefault();
    setSelectedFlag(row);

    if (row.students && row.students.length > 0) {
      setStudentData(row.students);
    }

    if (!(row.students && row.students.length > 0)) {
      const timesheet = timesheetSummaries?.filter(z => z.map(x => x.timesheetId === row.id && moment(row.date).isBetween(x.start, x.end)))[0][0];
      if (timesheet) {
        const week = timesheet?.weeks?.find(x => x.weekId === row.weekId);
        if (week && week.items && week.items.length > 0) {
          setStudentData(week?.items[0].studentData ?? []);
        }
      }
    }

    if (row.type == "entry") {
      setTask(row.fullTask);
      setEntry(row.fullEntry);
      setEntryFlagAction("reject_with_feedback");
      setEntryFlag(row.fullEntryFlag);
    } else {
      setTimesheetFlag(row.fullTimesheetFlag);
    }
    setOpenReviewEntryModal(true);
  };

  const handleApprove = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, row: FlagRow) => {
    e.preventDefault();
    setSelectedFlag(row);
    if (row.type === "entry") {
      setTask(row.fullTask);
      setEntry(row.fullEntry);
      setEntryFlagAction("approve");
      setEntryFlag(row.fullEntryFlag);
    } else {
      setTimesheetFlag(row.fullTimesheetFlag);
    }

  };

  useEffect(() => {
    if (entryFlag != undefined) {
      if (entryFlagAction === "approve") {
        approve.run();
      } else if (entryFlagAction === "reject") {
        reject.run();
      }
    }
  }, [entryFlag]);

  const handleReject = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, row: FlagRow) => {
    e.preventDefault();
    setSelectedFlag(row);
    if (row.type === "entry") {
      setTask(row.fullTask);
      setEntry(row.fullEntry);
      setEntryFlagAction("reject");
      setEntryFlag(row.fullEntryFlag);
    } else {
      setTimesheetFlag(row.fullTimesheetFlag);
    }
  };

  /**
   * Rejection Logic
   */
  const doReject = async () => {
    if (entryFlag === undefined)
      return Promise.reject("Entry Flag was missing");

    //Create our request
    const request: TimesheetEntryFlagRequest = {
      timesheetEntryFlagId: entryFlag.timesheetEntryFlagId!,
      timesheetEntryFlagStatusId: TimesheetFlagStatus.Rejected,
      timesheetWeekId: selectedFlag!.weekId,
      feedback: feedback
    }

    return updateFlag(request);

  }
  const reject = useAsync({
    deferFn: doReject,
    onResolve: (response) => {
      const currentEntryFlag = entryFlag;
      setEntryFlag(undefined);
      setFeedback('');
      const checkboxes = document.querySelectorAll('.datatable-checkbox');
      checkboxes.forEach((checkbox: any) => {
        if (checkbox.checked) {
          checkbox.click();
        }
      });

      //Update the flags by removing the rejected flag
      setFlags([...flags.filter(x => x.id !== currentEntryFlag?.timesheetEntryFlagId)]);
    }
  });

  /**
   * Approval Logic
   */
  const doApprove = async () => {
    if (entryFlag === undefined)
      return Promise.reject("Entry Flag was missing");

    //Create our request
    const request: TimesheetEntryFlagRequest = {
      timesheetEntryFlagId: entryFlag.timesheetEntryFlagId!,
      timesheetEntryFlagStatusId: TimesheetFlagStatus.Approved,
      timesheetWeekId: selectedFlag!.weekId,
      feedback: feedback
    }

    updateFlag(request);
  }
  const approve = useAsync({
    deferFn: doApprove,
    onResolve: (response) => {
      const currentEntryFlag = entryFlag;
      setEntryFlag(undefined);
      setFeedback('');
      const checkboxes = document.querySelectorAll('.datatable-checkbox');
      checkboxes.forEach((checkbox: any) => {
        if (checkbox.checked) {
          checkbox.click();
        }
      });

      //Update the flags by removing the approved flag
      setFlags([...flags.filter(x => x.id !== currentEntryFlag?.timesheetEntryFlagId)]);
    }
  });

  /**
   * Render
   */
  return (
    <Layout
      isLoading={
        weekData.isPending ||
        approve.isPending ||
        approveMulti.isPending ||
        reject.isPending ||
        rejectMulti.isPending
      }>
      <div>
        <EditModal
          open={openReviewEntryModal}
          allowSave={false}
          saveColor='bg-green-500 hover:bg-green-500'
          removeText='Reject'
          removeColor={feedback && feedback !== '' ? 'text-white bg-red-500 hover:bg-red-500' : `text-gray bg-white-500 hover:bg-white-500`}
          onRemove={() => {
            if (feedback && feedback !== '') {
              reject.run();
              setFeedback('');
              setOpenReviewEntryModal(false)
            }
          }}
          onCancel={() => { setFeedback(''); setOpenReviewEntryModal(false) }}
        >
          <Fragment>
            <h3>Review Entry</h3>

            {entryFlag && (
              <Fragment>
                <div className='grid grid-cols-3 gap-4 mt-2'>
                  <div className=''>
                    <div className='text-sm font-bold'>Therapist</div>
                    <div className='text-xs'>
                      <div>{selectedFlag?.therapist}</div>
                      <div className='italic'>{selectedFlag?.discipline}</div>
                    </div>
                  </div>
                  <div className=''>
                    <div className='text-sm font-bold'>School</div>
                    <div>
                      <div className='text-sm'>{
                        task?.subTitle === null ||
                          task?.subTitle === undefined ||
                          task?.subTitle === '' ?
                          (task?.schoolName === undefined ||
                            task?.schoolName === null ? 'N/A' :
                            task?.schoolName) :
                          task?.subTitle}
                      </div>
                      <div className='text-sm'>{
                        district ? district.districtName : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className=''>
                    <div className='text-sm font-bold'>Rule</div>
                    <div className='text-sm font-bold text-red-500'>
                      {entryFlag && entryFlag !== undefined ?
                        entryFlag.proofingRule.name
                        :
                        ((timesheetFlag && timesheetFlag !== undefined) &&
                          (timesheetFlag.proofingRule && timesheetFlag.proofingRule !== undefined) ?
                          timesheetFlag.proofingRule.name : '')
                      }
                    </div>
                  </div>
                </div>

                <div className='grid grid-cols-3 gap-4 mt-2'>
                  <div className=''>
                    <div className='text-sm font-bold'>Task Type</div>
                    <div className='text-xs'>
                      {task?.taskTypeName !== '' ? task?.taskTypeName : 'N/A'}
                    </div>
                  </div>

                  {studentData && studentData.length > 0 &&
                    task?.taskTypeId !== TaskTypeLookup.ClinicalSupervision &&
                    task?.taskTypeId !== TaskTypeLookup.Mtss &&
                    task?.taskTypeId !== TaskTypeLookup.Other && <div className=''>
                      <div className='text-sm font-bold'>Student{studentData && studentData?.length > 0 ? '(s)' : ''}</div>
                      <div className='text-xs'>
                        {studentData && removeDuplicates(studentData, 'studentId').map((student) => (
                          <div key={`student_${student.studentId}`}>{student.lastName}, {student.firstName} (Grade: {student.grade})</div>
                        ))}
                      </div>
                    </div>}

                  <div className=''>
                    <div className='text-sm font-bold'>Time</div>
                    <div className='text-xs'>
                      {entry?.minutes && entry?.minutes !== null ? `${entry?.minutes} minutes` : `N/A`}
                    </div>
                  </div>

                  <div className='grid w-full gap-4'>
                    <div className=''>
                      <div className='text-sm font-bold'>Flag Status</div>
                      <div className='text-sm font-bold text-red-500'>{entryFlag.timesheetFlagStatus.name && entryFlag.timesheetFlagStatus.name !== null ? entryFlag.timesheetFlagStatus.name : 'N/A'}</div>
                    </div>
                  </div>

                  <div className=''>
                    <div className='text-sm font-bold'>Therapist Notes</div>
                    <div className='text-xs'>
                      <div>{entry?.notes}</div>
                    </div>
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-4 mt-2 mb-2'>
                  <div className='w-full flex-column'>
                    <div className='text-sm font-bold'>Feedback <span className="text-red-500 sups">*</span></div>
                    <div className='w-full'>
                      <textarea
                        className='w-full'
                        onChange={(e) => { setFeedback(e.currentTarget.value) }}
                        value={feedback ?? ''}
                      />
                    </div>
                  </div>
                </div>
              </Fragment>
            )}

          </Fragment>
        </EditModal>
        <h1>{district?.districtName}</h1>
        <p className="text-sm font-bold">Currently Viewing</p>
        <div>
          <span className="text-3xl font-bold">{viewingStringFrom}</span>
          <span className="text-2xl"> to </span>
          <span className="text-3xl font-bold">{viewingStringTo}</span>
        </div>
        <div className='w-full mt-2'>
          <div className='z-0 px-4 py-5 bg-white sm:p-6'>
            <div className='z-0 grid grid-cols-6 gap-6'>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Therapist</label>
                <SelectMulti
                  className='block primary'
                  isMulti={false}
                  options={therapistOptions}
                  isClearable={true}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: "-1" };
                    setTherapist(e.value);
                  }}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Discipline</label>
                <select
                  className='block primary'
                  value={discipline}
                  onChange={(e) => setDiscipline(e.currentTarget.value)}
                  placeholder='Select...'
                >
                  <option value="-1"></option>
                  {disciplineOptions.sort((a, b) => {
                    return a.label.localeCompare(b.label);
                  }).map((item, idx) => <option key={`pageSize_${item.label}`}>{item.label}</option>)}
                </select>
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Flag</label>
                <SelectMulti
                  className='block primary'
                  isMulti={false}
                  options={flagOptions}
                  isClearable={true}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: "-1" };
                    setFlag(e.value);
                  }}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>School</label>
                <SelectMulti
                  className='block primary'
                  isMulti={false}
                  options={schoolOptions}
                  isClearable={true}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: "-1" };
                    setSchool(e.value);
                  }}
                />
              </div>

            </div>
          </div>
          <div className='flex px-4 py-3 text-right bg-gray-50 sm:px-6'>
            <SubmitButton
              text='Search'
              className='mr-5 bg-ptsblue-normal hover:bg-ptsblue-dark'
              onClick={() => {
                handleSearchAsync.run();
              }}
            />
          </div>
          <div className='p-3 mt-5 bg-white rounded-lg shadow'>
            <button className="mr-5 bg-ptsblue-normal hover:bg-ptsblue-dark"
              disabled={selectedCount === 0}
              onClick={() => {
                approveMulti.run();
              }}
            >
              Approve Selected
            </button>
            <button className="mr-5 bg-ptsblue-normal hover:bg-ptsblue-dark"
              disabled={selectedCount === 0}
              onClick={() => {
                rejectMulti.run();
              }}
            >
              Reject Selected
            </button>
            <div className='mt-5'>
              <DataTable
                columns={columns}
                data={flags!}
                initialState={initialState}
                showPagination={true}
                resetOnDataChange={false}
                getRowProps={(row: any) => ({
                  className: row.original.isMyMessage
                    ? 'cursor-pointer hover:bg-purple-300 bg-purple-200'
                    : 'cursor-pointer hover:bg-gray-100',
                  onClick: () => { }
                  //history.push(`/admin/districts/${row.values.districtId}`),
                })}
              />
            </div>
            {/* Week (Timesheet) Display */}

          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ProofingSummary;

